import React from "react";
import BackendLocationSelect from "../../../../components/Forms/BackendLocationSelect/BackendLocationSelect.tsx";
import SectionHeader from "../../../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../../../constants/formStyles.ts";
import {
  PARTY_KEYS,
  PARTY_LABELS,
  type Party,
  type PartyField,
  type PartyKey,
} from "../model.ts";
import type { LifecycleController } from "../useLifecycle.ts";
import { CARD } from "./shared.tsx";
import { RecordStatus } from "./RecordStatus.tsx";

export function PartyCards({
  controller,
}: {
  controller: LifecycleController;
}) {
  const { draft, quoteForm, leasing, lesseeMissing, driverPinLooksWrong, jwt } =
    controller;
  const { manual, resolvedRoles, partyEdits, bufferParty } = quoteForm;
  const [expanded, setExpanded] = React.useState<
    Partial<Record<PartyKey, boolean>>
  >({});
  return (
    <>
      {draft && (
        <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-3">
          {PARTY_KEYS.map((party) => {
            const p = draft[party];
            const buffer = partyEdits[party];
            const view = buffer?.values ?? p;
            const record = controller.records.parties[party];
            const changed = Boolean(buffer) || record.changed;
            const edit = (patch: Partial<Party>) => bufferParty(party, patch);
            const discard = () => quoteForm.discardParty(party);
            const save = () => controller.records.saveParty(party);
            // The header reflects the committed draft; the form, the buffer.
            const displayLegal = p.customerType === "legal";
            const display = displayLegal
              ? p.companyName || "—"
              : [p.firstName, p.surname, p.familyName]
                  .filter(Boolean)
                  .join(" ") || "—";
            const isLegal = view.customerType === "legal";
            const edited = Object.keys(manual).some((k) =>
              k.startsWith(`${party}.`)
            );
            // All three cards are open on first render so the desktop row uses
            // its width productively. Each can still be collapsed independently.
            const open = expanded[party] ?? true;

            const f = (key: PartyField, label: string) => (
              <div>
                <label htmlFor={`${party}.${key}`} className={labelClass}>
                  {label}
                </label>
                <input
                  id={`${party}.${key}`}
                  className={inputClass}
                  value={String(view[key] ?? "")}
                  onChange={(e) => edit({ [key]: e.target.value })}
                />
              </div>
            );

            return (
              <fieldset
                disabled={controller.busy !== null || controller.records.saving}
                aria-label={PARTY_LABELS[party]}
                key={party}
                className={`${CARD} min-w-0 overflow-hidden ${
                  party === "holder" && lesseeMissing ? "!border-warning" : ""
                }`}
              >
                <SectionHeader
                  title={
                    party === "holder" && leasing?.detected
                      ? "Застраховащ / Лизингополучател"
                      : PARTY_LABELS[party]
                  }
                />
                <div className="p-4 pt-2">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() =>
                      setExpanded((x) => ({ ...x, [party]: !open }))
                    }
                    className="flex w-full items-start justify-between gap-3 border-b border-stroke pb-3 text-left dark:border-strokedark"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-black dark:text-white">
                        {display}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-body dark:text-bodydark">
                        {p.pin}
                      </span>
                      {edited && (
                        <span className="mt-1 block text-[10px] text-warning">
                          Променен
                        </span>
                      )}
                      {changed && (
                        <span className="mt-1 block text-[10px] text-warning">
                          Незаписани промени
                        </span>
                      )}
                      {party === "driver" && driverPinLooksWrong && (
                        <span className="mt-1 block text-[10px] leading-4 text-[#AA2808]">
                          ЕГН на водача изглежда невалиден — ЕИК се таксува като
                          „неустановен водач“
                        </span>
                      )}
                      {party === "driver" && !edited && (
                        <span className="mt-1 block text-[10px] text-body dark:text-bodydark">
                          {resolvedRoles.user
                            ? "ползвател по данни от застрахователя"
                            : p.pin && p.pin === draft.owner.pin
                            ? "= собственика"
                            : ""}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 rounded bg-gray-2 px-2 py-1 text-[10px] font-medium text-black dark:bg-meta-4 dark:text-white">
                      {open ? "Скрий" : "Редактирай"}
                    </span>
                  </button>
                  <RecordStatus
                    state={record}
                    retry={() => controller.records.retryParty(party)}
                  />

                  {open && (
                    <div className="grid grid-cols-1 gap-x-3 gap-y-2 pt-3 2xl:grid-cols-2">
                      <div className="flex flex-wrap items-center gap-2 2xl:col-span-2">
                        <span className="text-[11px] text-body dark:text-bodydark">
                          Копирай от:
                        </span>
                        {PARTY_KEYS.filter((other) => other !== party).map(
                          (other) => (
                            <button
                              key={other}
                              type="button"
                              onClick={() =>
                                bufferParty(party, { ...draft[other] }, true)
                              }
                              disabled={
                                party === "driver" &&
                                draft[other].customerType === "legal"
                              }
                              className="rounded bg-gray-2 px-2 py-1 text-[11px] dark:bg-meta-4 dark:text-white"
                            >
                              {PARTY_LABELS[other]}
                            </button>
                          )
                        )}
                      </div>

                      {party !== "driver" && (
                        <div className="2xl:col-span-2">
                          <label
                            htmlFor={`${party}.customerType`}
                            className={labelClass}
                          >
                            Вид лице
                          </label>
                          <select
                            id={`${party}.customerType`}
                            className={inputClass}
                            value={view.customerType}
                            onChange={(e) =>
                              edit({
                                customerType: e.target.value as
                                  | "natural"
                                  | "legal",
                              })
                            }
                          >
                            <option value="natural">Физическо лице</option>
                            <option value="legal">Юридическо лице</option>
                          </select>
                        </div>
                      )}

                      {isLegal && party !== "driver" ? (
                        <div className="2xl:col-span-2">
                          {f("companyName", "Наименование")}
                        </div>
                      ) : (
                        <>
                          {f("firstName", "Име")}
                          {f("surname", "Презиме")}
                          {f("familyName", "Фамилия")}
                        </>
                      )}

                      {f(
                        "pin",
                        isLegal && party !== "driver" ? "ЕИК / БУЛСТАТ" : "ЕГН"
                      )}

                      <div className="2xl:col-span-2">
                        <BackendLocationSelect
                          jwt={jwt}
                          value={String(view.settlementCode ?? "")}
                          onChange={(code) => edit({ settlementCode: code })}
                        />
                      </div>
                      {f("street", "Адрес")}
                      {f("phone", "Телефон")}

                      {changed && (
                        <div className="flex justify-end gap-2 pt-1 2xl:col-span-2">
                          {buffer && (
                            <button
                              type="button"
                              onClick={discard}
                              className="rounded bg-gray-2 px-3 py-1.5 text-xs font-medium text-black dark:bg-meta-4 dark:text-white"
                            >
                              Отказ
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={save}
                            disabled={
                              record.loading ||
                              !record.loaded ||
                              Boolean(record.validation)
                            }
                            className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-opacity-90 disabled:opacity-50"
                          >
                            {record.changed ? "Запиши" : "Приложи"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </fieldset>
            );
          })}
        </div>
      )}
    </>
  );
}
